import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import bukkie from "@/ds/vendor/assets/characters/Bukkie.svg";
import happyPropal from "@/ds/vendor/assets/characters/Happy Propal.svg";
import "./celebrate.css";

// Two small, earned moments (design README: expressive motion is sparing and
// moment-based in product). Never blocks the UI, never repeats in a burst,
// and reduced-motion users get a still version of the same message.
//
//   visit → the farm Bukkie drives across the bottom of the screen and parks
//           a "visit wrapped up" note behind it.
//   task  → Happy Propal peeks up from the corner, bobs once, ducks back.

type Moment = { kind: "visit" | "task"; title: string; sub?: string; key: number };
const Ctx = createContext<(m: Omit<Moment, "key">) => void>(() => {});
export const useCelebrate = () => useContext(Ctx);

export function CelebrationProvider({ children }: { children: ReactNode }) {
  const [visit, setVisit] = useState<Moment | null>(null);
  const [task, setTask] = useState<Moment | null>(null);
  const last = useRef<Record<string, number>>({});
  const timers = useRef<Record<string, number>>({});

  const fire = useCallback((m: Omit<Moment, "key">) => {
    const now = Date.now();
    // One moment at a time per kind; a burst of completions collapses into one.
    if (now - (last.current[m.kind] || 0) < 1200) return;
    last.current[m.kind] = now;
    const moment = { ...m, key: now };
    const set = m.kind === "visit" ? setVisit : setTask;
    set(moment);
    clearTimeout(timers.current[m.kind]);
    timers.current[m.kind] = window.setTimeout(() => set(null), m.kind === "visit" ? 4600 : 2600);
  }, []);

  return (
    <Ctx.Provider value={fire}>
      {children}
      <div className="moments" aria-live="polite">
        {visit && (
          <div className="moment-visit" key={visit.key}>
            <div className="moment-road" aria-hidden="true">
              <div className="moment-bukkie">
                <img src={bukkie} alt="" />
                <i className="dust d1" /><i className="dust d2" /><i className="dust d3" />
              </div>
            </div>
            <div className="moment-note pm-glass">
              <span className="moment-check">✓</span>
              <span><strong>{visit.title}</strong>{visit.sub && <span className="moment-sub">{visit.sub}</span>}</span>
            </div>
          </div>
        )}
        {task && (
          <div className="moment-task" key={task.key}>
            <div className="moment-bubble pm-glass"><strong>{task.title}</strong>{task.sub && <span className="moment-sub">{task.sub}</span>}</div>
            <img className="moment-propal" src={happyPropal} alt="" aria-hidden="true" />
          </div>
        )}
      </div>
    </Ctx.Provider>
  );
}
